import {
  Checkbox,
  DatePicker,
  DateRange,
  Select,
  ThemeProvider,
} from "@veneer/core";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import TextField from "@mui/material/TextField";
import useSearchSelect  from "../../../hooks/searchSelect/useSearchSelect";
import { MAX_AGENDA_RANGE_MONTHS } from "../agendaConstants";
import { truncatePosLabel } from "../../../utils/conversion";

// Crear un componente común para los bloques compartidos
export const AgendaCreationBlock = (props: any) => {
  const {
    typeOfVisitID,
    labelSubretail,
    handleChangeSubRetail,
    subretailData,
    subRetail,
    onClearSubRetail,
    disableSubretail,
    labelPointOfSale,
    handleChangePointOfSale,
    pointOfSaleData,
    pointOfSale,
    setPointOfSale,
    disablePointOfSale,
    errorPos,
    endDate,
    handleChangeStartDate,
    startDate,
    errorDatePicker,
    theme,
    labelStartTime,
    startTime,
    handleChangeStartTime,
    labelEndTime,
    endTime,
    labelStardDate,
    labelEndDate,
    handleChangeEndTime,
    labelMonday,
    checkboxMonday,
    onChangeCheckboxCreateAgenda,
    labelTuesday,
    checkboxTuesday,
    labelWednesday,
    checkboxWednesday,
    labelThursday,
    checkboxThursday,
    labelFriday,
    checkboxFriday,
    labelSaturday,
    checkboxSaturday,
    labelSunday,
    checkboxSunday,
    labelVisitDate,
    visitDate,
    handleChangePointOfSaleList,
    handleChangeSubRetailList,
    subretailDataCopy,
    setSubretailData,
    pointOfSaleDataCopy,
    setPointOfSaleData,
    startTimeErrorReason,
    setStartTimeErrorReason,
    endTimeErrorReason,
    setEndTimeErrorReason,
  } = props;
  const { handleSearch } = useSearchSelect();
  const [t] = useTranslation("global");

  const timesAreEqual =
    startTime !== null && moment(startTime).isValid() &&
    endTime !== null && moment(endTime).isValid() &&
    moment(endTime).isSame(moment(startTime), 'minute');
  // Direct comparison so validation stays correct when startTime changes after endTime is set
  const endTimeIsBeforeStart =
    startTime !== null && moment(startTime).isValid() &&
    endTime !== null && moment(endTime).isValid() &&
    moment(endTime).isBefore(moment(startTime), 'minute');
  const endTimeInvalid = timesAreEqual || endTimeIsBeforeStart;
  let endTimeErrorMessage = t("header.endTimeInvalidError");
  if (timesAreEqual) {
    endTimeErrorMessage = t("header.startEndTimeSameError");
  } else if (endTimeErrorReason === 'minTime' || endTimeIsBeforeStart) {
    endTimeErrorMessage = t("header.endTimeBeforeStartError");
  }

  // Explicitly disable clock hours falling entirely before startTime + 5 min
  // Use format-aware parse: plain 'H:mm' strings fail moment() without a format
  const endShouldDisableTime = (value: number, clockType: string) => {
    const st = moment.isMoment(startTime) ? startTime : moment(String(startTime), ['HH:mm', 'H:mm']);
    if (!startTime || !st.isValid()) return false;
    if (clockType === 'hours') {
      const minTotalMins = st.hours() * 60 + st.minutes() + 5;
      return (value * 60 + 59) < minTotalMins;
    }
    return false;
  };

  switch (typeOfVisitID) {
    case 1:
      return (
        <div className="createAgendaPermanent">
          <Select
            className="createAgendaSelect"
            label={labelSubretail}
            onChange={(e) => handleChangeSubRetail(e.value)}
            options={subretailData}
            value={subRetail}
            onClear={() => onClearSubRetail()}
            disabled={disableSubretail}
            onSearch={(query: string) =>
              handleSearch(query, subretailDataCopy, setSubretailData)
            }
            searchDelay={500}
            showSearch={true}
          />
          <Select
            className="createAgendaSelect"
            label={labelPointOfSale}
            onChange={(e) => handleChangePointOfSale(e.value)}
            options={pointOfSaleData.map(truncatePosLabel)}
            value={pointOfSale}
            onClear={() => setPointOfSale([])}
            disabled={disablePointOfSale}
            error={errorPos}
            onSearch={(query: string) =>
              handleSearch(query, pointOfSaleDataCopy, setPointOfSaleData)
            }
            searchDelay={500}
            showSearch={true}
          />

          <DatePicker
            className="createAgendaDatePicker"
            isOutsideRange={(day) =>
              day.isBefore(moment(), "day") ||
              day.isAfter(moment().add(MAX_AGENDA_RANGE_MONTHS, "months"), "day")
            }
            clearIcon={false}
            endDate={endDate}
            onChange={(date) => handleChangeStartDate(date as DateRange)}
            orientation="horizontal"
            range
            startDatePlaceholder="mm/dd/yyyy"
            endDatePlaceholder="mm/dd/yyyy"
            format="LL"
            startDate={startDate}
            startDateLabel={labelStardDate}
            endDateLabel={labelEndDate}
            error={errorDatePicker}
          />

          <div className="agendaTimesErrorBlock">
            {startTimeErrorReason && (
              <p className="agendaTimePickerError">
                {startTimeErrorReason === 'minTime'
                  ? t("header.startTimePastError")
                  : t("header.startTimeInvalidError")}
              </p>
            )}
            {(endTimeErrorReason || endTimeInvalid) && (
              <p className="agendaTimePickerError">{endTimeErrorMessage}</p>
            )}
          </div>

          <div className="createAgendaTimes">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <ThemeProvider theme={theme}>
                <div className="agendaTimePickerCell">
                  <TimePicker
                    className="createAgendaTimePicker"
                    label={labelStartTime}
                    value={startTime}
                    onChange={(value) => handleChangeStartTime(value)}
                    onError={(reason) => setStartTimeErrorReason(reason ?? null)}
                    renderInput={(params) => <TextField {...params} />}
                    minutesStep={5}
                    ampm={false}
                    minTime={startDate && moment(startDate).isSame(moment(), 'day') ? moment() : undefined}
                  />
                </div>

                <div className="agendaTimePickerCell">
                  <TimePicker
                    className="createAgendaTimePicker"
                    label={labelEndTime}
                    value={endTime}
                    disabled={!startTime}
                    minTime={startTime ? moment(startTime).add(5, 'minutes') : undefined}
                    shouldDisableTime={endShouldDisableTime}
                    onChange={(value) => handleChangeEndTime(value)}
                    onError={(reason) => setEndTimeErrorReason(reason ?? null)}
                    renderInput={(params) => (
                      <TextField {...params} error={params.error || endTimeInvalid} />
                    )}
                    minutesStep={5}
                    ampm={false}
                  />
                </div>
              </ThemeProvider>
            </LocalizationProvider>
          </div>

          <div className="createAgendaDays">
            <Checkbox
              className="createAgendaCheckbox"
              label={labelMonday}
              value={checkboxMonday}
              checked={checkboxMonday}
              onChange={() => onChangeCheckboxCreateAgenda(1)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelTuesday}
              value={checkboxTuesday}
              checked={checkboxTuesday}
              onChange={() => onChangeCheckboxCreateAgenda(2)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelWednesday}
              value={checkboxWednesday}
              checked={checkboxWednesday}
              onChange={() => onChangeCheckboxCreateAgenda(3)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelThursday}
              value={checkboxThursday}
              checked={checkboxThursday}
              onChange={() => onChangeCheckboxCreateAgenda(4)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelFriday}
              value={checkboxFriday}
              checked={checkboxFriday}
              onChange={() => onChangeCheckboxCreateAgenda(5)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelSaturday}
              value={checkboxSaturday}
              checked={checkboxSaturday}
              onChange={() => onChangeCheckboxCreateAgenda(6)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelSunday}
              value={checkboxSunday}
              checked={checkboxSunday}
              onChange={() => onChangeCheckboxCreateAgenda(7)}
            />
          </div>
        </div>
      );
    case 2:
    case 7:
      return (
        <div className="createAgendaVisit">
          <Select
            className="createAgendaSelect"
            label={labelSubretail}
            onChange={(e) => handleChangeSubRetail(e.value)}
            options={subretailData}
            value={subRetail}
            onClear={() => onClearSubRetail()}
            disabled={disableSubretail}
            onSearch={(query: string) =>
              handleSearch(query, subretailDataCopy, setSubretailData)
            }
            searchDelay={500}
            showSearch={true}
          />
          <Select
            className="createAgendaSelect"
            label={labelPointOfSale}
            onChange={(e) => handleChangePointOfSale(e.value)}
            options={pointOfSaleData.map(truncatePosLabel)}
            value={pointOfSale}
            onClear={() => setPointOfSale([])}
            disabled={disablePointOfSale}
            error={errorPos}
            onSearch={(query: string) =>
              handleSearch(query, pointOfSaleDataCopy, setPointOfSaleData)
            }
            searchDelay={500}
            showSearch={true}
          />

          <DatePicker
            className="createAgendaDatePicker"
            label={labelVisitDate}
            onChange={(date) => handleChangeStartDate(date as DateRange)}
            clearIcon={false}
            value={visitDate}
            placeholder="mm/dd/yyyy"
            format="L"
            isOutsideRange={(day) =>
              day.isBefore(moment(), "day") ||
              day.isAfter(moment().add(MAX_AGENDA_RANGE_MONTHS, "months"), "day")
            }
          />

          <div className="agendaTimesErrorBlock">
            {startTimeErrorReason && (
              <p className="agendaTimePickerError">
                {startTimeErrorReason === 'minTime'
                  ? t("header.startTimePastError")
                  : t("header.startTimeInvalidError")}
              </p>
            )}
            {(endTimeErrorReason || endTimeInvalid) && (
              <p className="agendaTimePickerError">{endTimeErrorMessage}</p>
            )}
          </div>

          <div className="createAgendaTimes">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <ThemeProvider theme={theme}>
                <div className="agendaTimePickerCell">
                  <TimePicker
                    className="createAgendaTimePicker"
                    label={labelStartTime}
                    value={startTime}
                    onChange={(value) => handleChangeStartTime(value)}
                    onError={(reason) => setStartTimeErrorReason(reason ?? null)}
                    renderInput={(params) => <TextField {...params} />}
                    minutesStep={5}
                    ampm={false}
                    minTime={visitDate && moment(visitDate).isSame(moment(), 'day') ? moment() : undefined}
                  />
                </div>

                <div className="agendaTimePickerCell">
                  <TimePicker
                    className="createAgendaTimePicker"
                    label={labelEndTime}
                    value={endTime}
                    disabled={!startTime}
                    minTime={startTime ? moment(startTime).add(5, 'minutes') : undefined}
                    shouldDisableTime={endShouldDisableTime}
                    onChange={(value) => handleChangeEndTime(value)}
                    onError={(reason) => setEndTimeErrorReason(reason ?? null)}
                    renderInput={(params) => (
                      <TextField {...params} error={params.error || endTimeInvalid} />
                    )}
                    minutesStep={5}
                    ampm={false}
                  />
                </div>
              </ThemeProvider>
            </LocalizationProvider>
          </div>
        </div>
      );
    case 3:
      return (
        <div className="createAgendaPermanent">
          <Select
            className="createAgendaSelect"
            label={labelSubretail}
            onChange={(e) => handleChangeSubRetailList(e.value)}
            options={subretailData}
            value={subRetail}
            onClear={() => onClearSubRetail()}
            disabled={disableSubretail}
            multiple
            onSearch={(query: string) =>
              handleSearch(query, subretailDataCopy, setSubretailData)
            }
            searchDelay={500}
            showSearch={true}
          />
          <Select
            className="createAgendaSelect"
            label={labelPointOfSale}
            onChange={(e) => handleChangePointOfSaleList(e.value)}
            options={pointOfSaleData.map(truncatePosLabel)}
            value={pointOfSale}
            onClear={() => setPointOfSale([])}
            disabled={disablePointOfSale}
            error={errorPos}
            multiple
            onSearch={(query: string) =>
              handleSearch(query, pointOfSaleDataCopy, setPointOfSaleData)
            }
            searchDelay={500}
            showSearch={true}
          />

          <DatePicker
            className="createAgendaDatePicker"
            isOutsideRange={(day) =>
              day.isBefore(moment(), "day") ||
              day.isAfter(moment().add(MAX_AGENDA_RANGE_MONTHS, "months"), "day")
            }
            clearIcon={false}
            endDate={endDate}
            onChange={(date) => handleChangeStartDate(date as DateRange)}
            orientation="horizontal"
            range
            startDatePlaceholder="mm/dd/yyyy"
            endDatePlaceholder="mm/dd/yyyy"
            format="LL"
            startDate={startDate}
            startDateLabel={labelStardDate}
            endDateLabel={labelEndDate}
            error={errorDatePicker}
          />

          <div className="agendaTimesErrorBlock">
            {startTimeErrorReason && (
              <p className="agendaTimePickerError">
                {startTimeErrorReason === 'minTime'
                  ? t("header.startTimePastError")
                  : t("header.startTimeInvalidError")}
              </p>
            )}
            {(endTimeErrorReason || endTimeInvalid) && (
              <p className="agendaTimePickerError">{endTimeErrorMessage}</p>
            )}
          </div>

          <div className="createAgendaTimes">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <ThemeProvider theme={theme}>
                <div className="agendaTimePickerCell">
                  <TimePicker
                    className="createAgendaTimePicker"
                    label={labelStartTime}
                    value={startTime}
                    onChange={(value) => handleChangeStartTime(value)}
                    onError={(reason) => setStartTimeErrorReason(reason ?? null)}
                    renderInput={(params) => <TextField {...params} />}
                    minutesStep={5}
                    ampm={false}
                    minTime={startDate && moment(startDate).isSame(moment(), 'day') ? moment() : undefined}
                  />
                </div>

                <div className="agendaTimePickerCell">
                  <TimePicker
                    className="createAgendaTimePicker"
                    label={labelEndTime}
                    value={endTime}
                    disabled={!startTime}
                    minTime={startTime ? moment(startTime).add(5, 'minutes') : undefined}
                    shouldDisableTime={endShouldDisableTime}
                    onChange={(value) => handleChangeEndTime(value)}
                    onError={(reason) => setEndTimeErrorReason(reason ?? null)}
                    renderInput={(params) => (
                      <TextField {...params} error={params.error || endTimeInvalid} />
                    )}
                    minutesStep={5}
                    ampm={false}
                  />
                </div>
              </ThemeProvider>
            </LocalizationProvider>
          </div>

          <div className="createAgendaDays">
            <Checkbox
              className="createAgendaCheckbox"
              label={labelMonday}
              value={checkboxMonday}
              checked={checkboxMonday}
              onChange={() => onChangeCheckboxCreateAgenda(1)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelTuesday}
              value={checkboxTuesday}
              checked={checkboxTuesday}
              onChange={() => onChangeCheckboxCreateAgenda(2)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelWednesday}
              value={checkboxWednesday}
              checked={checkboxWednesday}
              onChange={() => onChangeCheckboxCreateAgenda(3)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelThursday}
              value={checkboxThursday}
              checked={checkboxThursday}
              onChange={() => onChangeCheckboxCreateAgenda(4)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelFriday}
              value={checkboxFriday}
              checked={checkboxFriday}
              onChange={() => onChangeCheckboxCreateAgenda(5)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelSaturday}
              value={checkboxSaturday}
              checked={checkboxSaturday}
              onChange={() => onChangeCheckboxCreateAgenda(6)}
            />
            <Checkbox
              className="createAgendaCheckbox"
              label={labelSunday}
              value={checkboxSunday}
              checked={checkboxSunday}
              onChange={() => onChangeCheckboxCreateAgenda(7)}
            />
          </div>
        </div>
      );
    default:
      return null;
  }
};